import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Image, Trash2, Download, Plus, Loader2, AlertCircle, 
  Maximize2, X, Wand2 
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useProject } from '@/context/ProjectContext';
import { toast } from 'sonner';
import ProviderBadge from '@/components/ProviderBadge';
import ReportButton from '@/components/ReportButton';

const AssetsPanel = () => {
  const { assets, generateImage, deleteAsset, loading, apiStatus, currentProject } = useProject();
  
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [assetType, setAssetType] = useState('general');
  const [generating, setGenerating] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error('Please enter a prompt');
      return;
    }

    try {
      setGenerating(true);
      await generateImage(prompt, assetType);
      toast.success('Asset generated successfully!');
      setShowGenerateDialog(false);
      setPrompt('');
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to generate image';
      toast.error(msg);
    } finally {
      setGenerating(false);
    }
  };

  const handleDelete = async (assetId) => {
    try {
      setDeletingId(assetId);
      await deleteAsset(assetId);
      toast.success('Asset deleted');
      if (selectedAsset?.id === assetId) {
        setSelectedAsset(null);
      }
    } catch (error) {
      toast.error('Failed to delete asset');
    } finally {
      setDeletingId(null);
    }
  };

  const handleDownload = (url, name) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = name || 'asset';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const assetTypes = [
    { value: 'general', label: 'General' },
    { value: 'logo', label: 'Logo' },
    { value: 'banner', label: 'Banner' },
    { value: 'social', label: 'Social Media' },
    { value: 'packaging', label: 'Packaging' }
  ];

  const groupedAssets = assets.reduce((acc, asset) => {
    const type = asset.asset_type || 'general';
    if (!acc[type]) acc[type] = [];
    acc[type].push(asset);
    return acc;
  }, {});

  return (
    <div className="h-full overflow-auto p-6" data-testid="assets-panel">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="font-heading text-2xl text-foreground mb-2">Asset Library</h2>
            <p className="text-muted-foreground">
              {assets.length} asset{assets.length !== 1 ? 's' : ''} generated
            </p>
          </div>
          <div className="flex items-center gap-2">
            <ReportButton section="assets" projectId={currentProject?.id} />
            <Button
              onClick={() => setShowGenerateDialog(true)}
              disabled={!apiStatus.fal}
              className="bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              data-testid="generate-asset-btn"
            >
              <Wand2 className="w-4 h-4 mr-2" />
              Generate Asset
            </Button>
          </div>
        </div>

        {/* API Warning */}
        {!apiStatus.fal && (
          <div className="mb-6 p-4 rounded-xl bg-yellow-500/10 border border-yellow-500/20">
            <div className="flex items-center gap-2 text-yellow-700">
              <AlertCircle className="w-5 h-5" />
              <span>Fal.ai API key not configured. Add FAL_KEY to backend/.env to enable image generation.</span>
            </div>
          </div>
        )}

        {/* Empty State */}
        {assets.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-16"
          >
            <div className="w-20 h-20 rounded-2xl bg-black/[0.03] flex items-center justify-center mx-auto mb-6">
              <Image className="w-10 h-10 text-muted-foreground/70" />
            </div>
            <h3 className="font-heading text-xl text-foreground mb-2">No Assets Yet</h3>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              Generate your first asset using AI. Create logos, banners, social media graphics, and more.
            </p>
            <Button
              onClick={() => setShowGenerateDialog(true)}
              disabled={!apiStatus.fal}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
              data-testid="first-asset-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Your First Asset
            </Button>
          </motion.div>
        )}

        {/* Asset Grid by Type */}
        {Object.entries(groupedAssets).map(([type, typeAssets]) => (
          <div key={type} className="mb-10">
            <h3 className="font-heading text-lg text-foreground mb-4 capitalize flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-primary" />
              {type === 'general' ? 'General Assets' : `${type} Assets`}
              <span className="text-sm text-muted-foreground font-normal">({typeAssets.length})</span>
            </h3>
            
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              <AnimatePresence>
                {typeAssets.map((asset, index) => (
                  <motion.div
                    key={asset.id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ delay: index * 0.05 }}
                    className="group relative aspect-square rounded-xl overflow-hidden border border-border bg-black/[0.03]"
                    data-testid={`asset-card-${asset.id}`}
                  >
                    <img
                      src={asset.url}
                      alt={asset.prompt}
                      className="w-full h-full object-cover transition-transform group-hover:scale-105"
                    />
                    
                    {/* Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="absolute bottom-0 left-0 right-0 p-3">
                        <p className="text-xs text-white/80 truncate mb-2">{asset.prompt}</p>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setSelectedAsset(asset)}
                            className="flex-1 h-8 text-xs bg-white/10 hover:bg-white/20 text-white"
                            data-testid={`view-asset-${asset.id}`}
                          >
                            <Maximize2 className="w-3 h-3 mr-1" />
                            View
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDownload(asset.url, `${type}-${asset.id}`)}
                            className="h-8 w-8 bg-white/10 hover:bg-white/20 text-white"
                            data-testid={`download-asset-${asset.id}`}
                          >
                            <Download className="w-3 h-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDelete(asset.id)}
                            disabled={deletingId === asset.id}
                            className="h-8 w-8 bg-white/10 hover:bg-red-500/50 text-white"
                            data-testid={`delete-asset-${asset.id}`}
                          >
                            {deletingId === asset.id ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <Trash2 className="w-3 h-3" />
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </div>
        ))}
      </div>

      {/* Generate Dialog */}
      <Dialog open={showGenerateDialog} onOpenChange={setShowGenerateDialog}>
        <DialogContent className="glass border-border bg-card text-foreground max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-foreground">Generate Asset</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 pt-4">
            <div className="space-y-2">
              <Label className="text-foreground/80">Asset Type</Label>
              <Select value={assetType} onValueChange={setAssetType}>
                <SelectTrigger 
                  className="bg-black/[0.03] border-border text-foreground"
                  data-testid="asset-type-select"
                >
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  {assetTypes.map(type => (
                    <SelectItem 
                      key={type.value} 
                      value={type.value}
                      className="text-foreground hover:bg-black/[0.06]"
                    >
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-foreground/80">Prompt</Label>
              <Textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Describe what you want to generate..."
                className="min-h-[120px] bg-black/[0.03] border-border text-foreground placeholder:text-muted-foreground/70"
                data-testid="generate-prompt-input"
              />
              <p className="text-xs text-muted-foreground">
                Be specific about colors, style, and composition for best results.
              </p>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowGenerateDialog(false)}
                className="flex-1 bg-black/[0.03] border-border text-foreground hover:bg-black/[0.06]"
                data-testid="cancel-generate-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={handleGenerate}
                disabled={generating || !prompt.trim()}
                className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90"
                data-testid="submit-generate-btn"
              >
                {generating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Wand2 className="w-4 h-4 mr-2" />
                    Generate
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Asset Preview Dialog */}
      <Dialog open={!!selectedAsset} onOpenChange={() => setSelectedAsset(null)}>
        <DialogContent className="glass border-border bg-card text-foreground max-w-4xl p-0 overflow-hidden">
          <button
            onClick={() => setSelectedAsset(null)}
            className="absolute top-4 right-4 z-10 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
            data-testid="close-preview-btn"
          >
            <X className="w-5 h-5" />
          </button>
          
          {selectedAsset && (
            <div className="flex flex-col md:flex-row">
              <div className="flex-1 bg-black flex items-center justify-center p-4">
                <img
                  src={selectedAsset.url}
                  alt={selectedAsset.prompt}
                  className="max-w-full max-h-[70vh] object-contain"
                  data-testid="preview-image"
                />
              </div>
              <div className="w-full md:w-80 p-6 border-t md:border-t-0 md:border-l border-border">
                <h3 className="font-heading text-lg text-foreground mb-4">Asset Details</h3>
                
                <div className="space-y-4">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Type</p>
                    <p className="text-foreground capitalize">{selectedAsset.asset_type}</p>
                  </div>
                  
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Dimensions</p>
                    <p className="text-foreground">{selectedAsset.width} × {selectedAsset.height}</p>
                  </div>
                  
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Prompt</p>
                    <p className="text-foreground text-sm">{selectedAsset.prompt}</p>
                  </div>
                  
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Created</p>
                    <p className="text-foreground text-sm flex items-center gap-2">
                      {new Date(selectedAsset.created_at).toLocaleString()}
                      <ProviderBadge provider={selectedAsset.generated_by} />
                    </p>
                  </div>
                </div>

                <div className="mt-6 flex gap-2">
                  <Button
                    onClick={() => handleDownload(selectedAsset.url, `${selectedAsset.asset_type}-${selectedAsset.id}`)}
                    className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90"
                    data-testid="download-preview-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      handleDelete(selectedAsset.id);
                    }}
                    className="bg-black/[0.03] border-border text-foreground hover:bg-red-500/20 hover:border-red-500/50"
                    data-testid="delete-preview-btn"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AssetsPanel;
